import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { startOfWeek, startOfMonth, subWeeks, subMonths, format } from "date-fns";
import { BaseChart } from "src/components/charts/BaseOptionChart";
import { useTransactionTrends } from "src/hooks/useTransactionTrends.hook";
import CollapsibleCard from "./CollapsibleCard";
import TimeGranularitySelector from "./TimeGranularitySelector";
import { fEnergy, fMoneyShort } from "src/utils/formatNumber";

// Weekly/Monthly drive their own date range, independent of the top selector:
//   weekly  -> last 4 weeks, monthly -> last 6 months.
// Daily keeps using the range the parent passes in (the top time selector).
const WEEKS_BACK = 4;
const MONTHS_BACK = 6;

const granularityInterval = (granularity) => {
  const now = new Date();
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  if (granularity === "weekly") {
    const start = startOfWeek(subWeeks(now, WEEKS_BACK - 1), { weekStartsOn: 1 });
    return { start: start.toISOString(), end: end.toISOString(), type: "Last4Weeks" };
  }
  if (granularity === "monthly") {
    const start = startOfMonth(subMonths(now, MONTHS_BACK - 1));
    return { start: start.toISOString(), end: end.toISOString(), type: "Last6Months" };
  }
  // daily -> last 7 days (self-contained; no dependency on a global selector)
  const start = new Date(now);
  start.setDate(start.getDate() - 6);
  start.setHours(0, 0, 0, 0);
  return { start: start.toISOString(), end: end.toISOString(), type: "Last7Days" };
};

// Turn a bucket key from the API into a readable x-axis label.
const bucketLabel = (bucket, granularity) => {
  const d = new Date(`${bucket}T00:00:00`);
  if (isNaN(d.getTime())) return bucket;
  return granularity === "monthly" ? format(d, "MMM yyyy") : format(d, "MMM d");
};

// A reusable "trend over time" section used by Energy, Session and Revenue.
// It owns the Daily / Weekly / Monthly toggle and renders a collapsible card.

// Energy is in kWh — scale up to MWh / GWh for large values.
const fmtEnergy = fEnergy;

// Rupees — compact to Lakh (L) / Crore (Cr) for large values (Indian convention).
const fmtMoney = fMoneyShort;

const fmtCount = (v) => Math.round(Number(v) || 0).toLocaleString("en-IN");

// Maps each metric to a key in the theme chart palette (theme.palette.chart).
const METRIC_COLOR_KEY = { energy: "blue", sessions: "green", revenue: "violet" };

const METRIC_CONFIG = {
  energy: {
    seriesName: "Energy Consumed",
    chartType: "bar",
    color: "#3182CE",
    yAxisTitle: "Energy",
    formatValue: fmtEnergy,
    formatTotal: fmtEnergy,
    formatAxis: fmtEnergy,
  },
  sessions: {
    seriesName: "Sessions",
    chartType: "bar",
    color: "#38A169",
    yAxisTitle: "Sessions",
    formatValue: fmtCount,
    formatTotal: fmtCount,
    formatAxis: fmtCount,
  },
  revenue: {
    seriesName: "Revenue",
    chartType: "bar",
    color: "#805AD5",
    yAxisTitle: "Revenue",
    formatValue: fmtMoney,
    formatTotal: fmtMoney,
    formatAxis: fmtMoney,
  },
};

const buildOptions = (labels, config, theme) => ({
  chart: {
    type: config.chartType,
    toolbar: { show: false },
    zoom: { enabled: false },
    animations: { enabled: true },
    fontFamily:
      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  colors: [config.color],
  dataLabels: { enabled: false },
  stroke: config.chartType === "area" ? { curve: "smooth", width: 3 } : { show: false, width: 0 },
  fill:
    config.chartType === "area"
      ? { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.1, stops: [0, 100] } }
      : { type: "solid", opacity: 0.9 },
  plotOptions: { bar: { columnWidth: "55%", borderRadius: 4 } },
  markers: config.chartType === "area" ? { size: 4, strokeWidth: 2, hover: { size: 6 } } : { size: 0 },
  xaxis: {
    categories: labels,
    labels: {
      style: {
        fontSize: "11px",
        fontFamily:
          'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        colors: theme.palette.text.secondary,
      },
      rotate: labels.length > 12 ? -45 : 0,
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  yaxis: {
    title: {
      text: config.yAxisTitle,
      style: { fontSize: "12px", fontWeight: 500, color: theme.palette.text.secondary },
    },
    labels: {
      style: { fontSize: "11px", colors: theme.palette.text.secondary },
      formatter: config.formatAxis ? (v) => config.formatAxis(v) : undefined,
    },
  },
  grid: {
    show: true,
    borderColor: theme.palette.divider,
    strokeDashArray: 3,
    xaxis: { lines: { show: false } },
    yaxis: { lines: { show: true } },
  },
  legend: { show: false },
  tooltip: {
    style: {
      fontSize: "12px",
      fontFamily:
        'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    y: { formatter: (value) => config.formatValue(value) },
  },
});

const ConsumptionTrendChart = ({
  title,
  subtitle,
  icon = null,
  metric,
  teamId,
  timeInterval,
  isLoading = false,
  defaultGranularity = "daily",
  defaultCollapsed = false,
  withBarView = false,
  barViewCollapsed = false,
  compact = false,
}) => {
  const chartHeight = compact
    ? { xs: 200, sm: 220, md: 230 }
    : { xs: 250, sm: 300, md: 320 };
  const [granularity, setGranularity] = useState(defaultGranularity);
  const theme = useTheme();
  // Colour each metric from the shared theme chart palette so all dashboard
  // charts stay consistent (energy=blue, sessions=green, revenue=violet).
  const config = useMemo(() => {
    const base = METRIC_CONFIG[metric] || METRIC_CONFIG.energy;
    const key = METRIC_COLOR_KEY[metric] || "blue";
    const themeColor = theme.palette.chart?.[key]?.[0];
    return themeColor ? { ...base, color: themeColor } : base;
  }, [metric, theme]);
  // Bar rendering of the same metric (used for the optional collapsible bar view).
  const barConfig = useMemo(() => ({ ...config, chartType: "bar" }), [config]);

  // Weekly/Monthly drive their own wider range; daily uses the parent's interval
  // (the top time selector). All three hit the pre-aggregated trends endpoint, so
  // the browser never downloads raw transactions.
  const ownInterval = useMemo(() => granularityInterval(granularity), [granularity]);
  const effectiveInterval = ownInterval || timeInterval;

  const { data: trends, isFetching: trendsFetching } = useTransactionTrends({
    teamId,
    timeInterval: effectiveInterval,
    granularity,
    enabled: !!teamId,
  });

  const effectiveLoading = trendsFetching || (ownInterval === null && isLoading);

  const { labels, seriesData } = useMemo(() => {
    const rows = Array.isArray(trends) ? trends : [];
    return {
      labels: rows.map((r) => bucketLabel(r.bucket, granularity)),
      seriesData: rows.map((r) => Number(r[metric]) || 0),
    };
  }, [trends, granularity, metric]);

  const total = seriesData.reduce((sum, v) => sum + (Number(v) || 0), 0);
  const average = seriesData.length ? total / seriesData.length : 0;

  const options = useMemo(() => buildOptions(labels, config, theme), [labels, config, theme]);
  const barOptions = useMemo(() => buildOptions(labels, barConfig, theme), [labels, barConfig, theme]);
  const series = useMemo(
    () => [{ name: config.seriesName, data: seriesData }],
    [config.seriesName, seriesData]
  );

  const selector = (
    <TimeGranularitySelector value={granularity} onChange={setGranularity} />
  );

  return (
    <CollapsibleCard
      title={title}
      subtitle={subtitle}
      icon={icon}
      // In compact (3-in-a-row) mode the header has no room for the toggle, so
      // it moves into the body just above the chart.
      action={compact ? null : selector}
      defaultCollapsed={defaultCollapsed}
    >
      {effectiveLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
          <CircularProgress size={36} />
        </Box>
      ) : (
        <>
          {/* Average label + (in compact mode) the granularity toggle on the
              SAME line, with the value below. Keeping the toggle on the fixed-
              height label line (not next to the variable-width value) keeps the
              header height identical across cards, so the charts stay aligned. */}
          <Box sx={{ mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                minHeight: 36,
              }}
            >
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Average ({granularity})
              </Typography>
              {compact && selector}
            </Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: config.color, whiteSpace: "nowrap" }}
            >
              {config.formatTotal(average)}
            </Typography>
          </Box>
          <Box sx={{ width: "100%", height: chartHeight }}>
            {labels.length > 0 ? (
              <BaseChart
                key={`${metric}-${granularity}-${labels.length}`}
                options={options}
                series={series}
                type={config.chartType}
                height="100%"
                width="100%"
              />
            ) : (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                  fontSize: 14,
                }}
              >
                No data available for the selected period
              </Box>
            )}
          </Box>

          {withBarView && labels.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <CollapsibleCard
                title="Bar view"
                subtitle="Same data as a bar chart"
                defaultCollapsed={barViewCollapsed}
              >
                <Box sx={{ width: "100%", height: chartHeight }}>
                  <BaseChart
                    key={`${metric}-bar-${granularity}-${labels.length}`}
                    options={barOptions}
                    series={series}
                    type="bar"
                    height="100%"
                    width="100%"
                  />
                </Box>
              </CollapsibleCard>
            </Box>
          )}
        </>
      )}
    </CollapsibleCard>
  );
};

ConsumptionTrendChart.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  icon: PropTypes.node,
  metric: PropTypes.oneOf(["energy", "sessions", "revenue"]).isRequired,
  teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  timeInterval: PropTypes.object,
  isLoading: PropTypes.bool,
  defaultGranularity: PropTypes.oneOf(["daily", "weekly", "monthly"]),
  defaultCollapsed: PropTypes.bool,
  withBarView: PropTypes.bool,
  barViewCollapsed: PropTypes.bool,
  compact: PropTypes.bool,
};

export default ConsumptionTrendChart;
